import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Form, Button, Alert, Nav, Table, Badge, Modal, ProgressBar } from 'react-bootstrap';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { AuthorizationCheck } from '../../utils/AuthorizationCheck';

const DashboardSettings = ({ auth }) => {
  const location = useLocation();
  const history = useHistory();

  // Determine active tab from URL hash or default to profile
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '');
    if (hash.startsWith('organization')) return 'organization';
    return ['profile', 'organization'].includes(hash) ? hash : 'profile';
  };

  const getOrgSectionFromHash = () => {
    const hash = location.hash.replace('#', '');
    if (hash.startsWith('organization-')) {
      return hash.replace('organization-', '');
    }
    return 'settings';
  };

  const [activeTab, setActiveTab] = useState(getTabFromHash());
  const [orgSection, setOrgSection] = useState(getOrgSectionFromHash());

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [preferences, setPreferences] = useState({
    default_tenant_id: ''
  });
  const [tenants, setTenants] = useState([]);
  const [displayName, setDisplayName] = useState('');
  const [activeTenant, setActiveTenant] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Org Admin state
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgInvitations, setOrgInvitations] = useState([]);
  const [orgUsage, setOrgUsage] = useState(null);
  const [orgHistory, setOrgHistory] = useState([]);
  const [orgSettings, setOrgSettings] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [savingOrgConfig, setSavingOrgConfig] = useState(false);
  const [orgConfig, setOrgConfig] = useState({
    user_history_visibility: 'own_only',
    show_token_costs_to_users: true,
    default_invitation_days: 7,
    default_invitation_hours: 0,
    timezone: 'America/New_York'
  });

  // Modal state
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ display_name: '', role: 'user' });
  const [removeUserData, setRemoveUserData] = useState({ attribution_mode: 'keep', replacement_text: '' });
  const [inviteData, setInviteData] = useState({ email: '', role: 'user' });
  const [processing, setProcessing] = useState(false);

  // Check if user can manage the active tenant
  const canManageTenant = activeTenant && ['admin', 'manager'].includes(activeTenant.role);

  // Handle tab change - update URL hash
  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    if (tab === 'organization') {
      history.replace(`#organization-${orgSection}`);
    } else {
      history.replace(`#${tab}`);
    }
  };

  const handleOrgSectionChange = (section) => {
    setOrgSection(section);
    history.replace(`#organization-${section}`);
  };

  useEffect(() => {
    if (auth && auth.id) {
      fetchAllData();
    }
  }, [auth]);

  useEffect(() => {
    if (activeTab === 'organization' && canManageTenant) {
      fetchOrgSectionData(orgSection);
    }
  }, [activeTab, orgSection, canManageTenant]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch profile
      const profileResponse = await fetch('/api/user/profile', { credentials: 'include' });
      if (profileResponse.ok) {
        const data = await profileResponse.json();
        setProfile({
          first_name: data.profile.first_name || '',
          last_name: data.profile.last_name || '',
          email: data.profile.email || ''
        });
      }

      // Fetch preferences
      const prefsResponse = await fetch('/api/user/preferences', { credentials: 'include' });
      if (prefsResponse.ok) {
        const data = await prefsResponse.json();
        setPreferences({
          default_tenant_id: data.preferences.default_tenant_id || ''
        });
      }

      // Fetch tenants
      const tenantsResponse = await fetch('/api/user/tenants', { credentials: 'include' });
      if (tenantsResponse.ok) {
        const data = await tenantsResponse.json();
        setTenants(data.tenants || []);
        // Find active tenant, or fall back to the first tenant
        const active = data.tenants?.find(t => t.is_active) || data.tenants?.[0];
        setActiveTenant(active);
        setDisplayName(active?.display_name || '');
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgSectionData = async (section) => {
    try {
      setOrgLoading(true);
      setError(null);

      switch (section) {
        case 'users':
          const usersRes = await fetch('/api/tenant/users', { credentials: 'include' });
          if (usersRes.ok) {
            const data = await usersRes.json();
            setOrgUsers(data.users || []);
            setTenantInfo({ seat_count: data.seatCount, used_seats: data.usedSeats });
          }
          break;

        case 'invitations':
          const invRes = await fetch('/api/tenant/invitations', { credentials: 'include' });
          if (invRes.ok) {
            const data = await invRes.json();
            setOrgInvitations(data.invitations || []);
            setTenantInfo(data.tenant);
          }
          break;

        case 'usage':
          const usageRes = await fetch('/api/tenant/usage', { credentials: 'include' });
          if (usageRes.ok) {
            const data = await usageRes.json();
            setOrgUsage(data);
          }
          break;

        case 'history':
          const histRes = await fetch('/api/tenant/history', { credentials: 'include' });
          if (histRes.ok) {
            const data = await histRes.json();
            setOrgHistory(data.articles || []);
          }
          break;

        case 'settings':
        default:
          const settingsRes = await fetch('/api/tenant/settings', { credentials: 'include' });
          if (settingsRes.ok) {
            const data = await settingsRes.json();
            setOrgSettings(data.tenant);
            // Also populate editable config
            setOrgConfig({
              user_history_visibility: data.settings?.user_history_visibility || 'own_only',
              show_token_costs_to_users: data.settings?.show_token_costs_to_users !== false,
              default_invitation_days: data.settings?.default_invitation_expiration?.days || 7,
              default_invitation_hours: data.settings?.default_invitation_expiration?.hours || 0,
              timezone: data.tenant?.timezone || 'America/New_York'
            });
          }
          break;
      }
    } catch (err) {
      setError(`Failed to load ${section}`);
    } finally {
      setOrgLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSavingProfile(true);
      setError(null);

      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          first_name: profile.first_name,
          last_name: profile.last_name
        })
      });

      if (response.ok) {
        setSuccess('Profile updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      const response = await fetch('/api/user/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          default_tenant_id: preferences.default_tenant_id || null
        })
      });

      if (response.ok) {
        setSuccess('Preferences saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save preferences');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDisplayName = async (e) => {
    e.preventDefault();
    if (!activeTenant) return;

    try {
      setSavingDisplayName(true);
      setError(null);

      const response = await fetch('/api/user/display-name', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ display_name: displayName })
      });

      if (response.ok) {
        setSuccess('Display name updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update display name');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDisplayName(false);
    }
  };

  const handleSaveOrgConfig = async () => {
    try {
      setSavingOrgConfig(true);
      setError(null);

      const response = await fetch('/api/tenant/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_history_visibility: orgConfig.user_history_visibility,
          show_token_costs_to_users: orgConfig.show_token_costs_to_users,
          default_invitation_expiration: {
            days: orgConfig.default_invitation_days,
            hours: orgConfig.default_invitation_hours
          },
          timezone: orgConfig.timezone
        })
      });

      if (response.ok) {
        setSuccess('Organization settings saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save organization settings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingOrgConfig(false);
    }
  };

  // User management handlers
  const handleEditUser = async () => {
    if (!selectedUser) return;
    try {
      setProcessing(true);
      const response = await fetch(`/api/tenant/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editUserData)
      });
      if (response.ok) {
        setSuccess('User updated successfully');
        setShowEditUserModal(false);
        fetchOrgSectionData('users');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update user');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!selectedUser) return;
    try {
      setProcessing(true);
      const response = await fetch(`/api/tenant/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(removeUserData)
      });
      if (response.ok) {
        setSuccess('User removed from organization');
        setShowRemoveUserModal(false);
        fetchOrgSectionData('users');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to remove user');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleSendInvitation = async () => {
    try {
      setProcessing(true);
      const response = await fetch('/api/tenant/users/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inviteData)
      });
      if (response.ok) {
        setSuccess('Invitation sent successfully');
        setShowInviteModal(false);
        setInviteData({ email: '', role: 'user' });
        fetchOrgSectionData('invitations');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to send invitation');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const response = await fetch(`/api/tenant/invitations/${invitationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setSuccess('Invitation cancelled');
        fetchOrgSectionData('invitations');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to cancel invitation');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const response = await fetch(`/api/tenant/invitations/${invitationId}/resend`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setSuccess('Invitation resent');
        fetchOrgSectionData('invitations');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resend invitation');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditUserModal = (membership) => {
    setSelectedUser(membership);
    setEditUserData({ display_name: membership.display_name || '', role: membership.role });
    setShowEditUserModal(true);
  };

  const openRemoveUserModal = (membership) => {
    setSelectedUser(membership);
    setRemoveUserData({ attribution_mode: 'keep', replacement_text: '' });
    setShowRemoveUserModal(true);
  };

  // Helper functions
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'manager': return 'warning';
      default: return 'primary';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const formatNumber = (num) => (num || 0).toLocaleString();

  // Check authorization
  const renderPage = AuthorizationCheck(auth);
  if (!renderPage) {
    return null;
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Settings - Spawnry</title>
        </Helmet>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '100px 0 3rem'
        }}>
          <Container>
            <h1 style={{ color: 'white', fontWeight: '700' }}>Settings</h1>
          </Container>
        </div>
        <Container className="py-5 text-center">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading settings...</p>
        </Container>
      </>
    );
  }

  // Left nav for org admin
  const orgNavItems = [
    { key: 'settings', label: 'Settings', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> },
    { key: 'users', label: 'Users', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { key: 'invitations', label: 'Invitations', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
    { key: 'usage', label: 'Usage', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg> },
    { key: 'history', label: 'History', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
  ];

  // Render org section content
  const renderOrgSectionContent = () => {
    if (orgLoading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    switch (orgSection) {
      case 'users':
        return (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 style={{ fontWeight: '600', margin: 0 }}>Users</h5>
              {tenantInfo && orgUsers.length < tenantInfo.seat_count && (
                <Button variant="success" size="sm" onClick={() => { setInviteData({ email: '', role: 'user' }); setShowInviteModal(true); }}>
                  + Invite User
                </Button>
              )}
            </div>

            <Row className="mb-4">
              <Col md={4}>
                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#166534' }}>{orgUsers.length}</div>
                  <div style={{ color: '#166534', fontSize: '0.85rem' }}>Active Users</div>
                </div>
              </Col>
              <Col md={4}>
                <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>{tenantInfo ? tenantInfo.seat_count - orgUsers.length : '-'}</div>
                  <div style={{ color: '#1e40af', fontSize: '0.85rem' }}>Available Seats</div>
                </div>
              </Col>
              <Col md={4}>
                <div style={{ background: '#faf5ff', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6b21a8' }}>{orgUsers.filter(u => u.role === 'admin').length}</div>
                  <div style={{ color: '#6b21a8', fontSize: '0.85rem' }}>Admins</div>
                </div>
              </Col>
            </Row>

            <Table responsive hover>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th>User</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgUsers.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-4 text-muted">No users</td></tr>
                ) : orgUsers.map((m, idx) => (
                  <tr key={idx}>
                    <td>
                      <div style={{ fontWeight: '500' }}>{m.first_name} {m.last_name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{m.email}</div>
                    </td>
                    <td>{m.display_name || <span className="text-muted">Not set</span>}</td>
                    <td><Badge bg={getRoleBadgeVariant(m.role)}>{m.role}</Badge></td>
                    <td>{formatDate(m.joined_at)}</td>
                    <td>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEditUserModal(m)}>Edit</Button>
                      {m.id !== auth.id && (
                        <Button variant="outline-danger" size="sm" onClick={() => openRemoveUserModal(m)}>Remove</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        );

      case 'invitations':
        return (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 style={{ fontWeight: '600', margin: 0 }}>Invitations</h5>
              <Button variant="success" size="sm" onClick={() => { setInviteData({ email: '', role: 'user' }); setShowInviteModal(true); }}>
                + Send Invitation
              </Button>
            </div>

            <Table responsive hover>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Sent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgInvitations.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-4 text-muted">No pending invitations</td></tr>
                ) : orgInvitations.map((inv, idx) => (
                  <tr key={idx}>
                    <td>{inv.email}</td>
                    <td><Badge bg={getRoleBadgeVariant(inv.role)}>{inv.role}</Badge></td>
                    <td>
                      <Badge bg={inv.status === 'pending' ? 'warning' : inv.status === 'accepted' ? 'success' : 'secondary'}>
                        {inv.status}
                      </Badge>
                    </td>
                    <td>{formatDate(inv.created_at)}</td>
                    <td>
                      {inv.status === 'pending' && (
                        <>
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleResendInvitation(inv.id)}>Resend</Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleCancelInvitation(inv.id)}>Cancel</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        );

      case 'usage':
        const usagePercent = orgUsage?.tenant ? Math.round(((orgUsage.tenant.monthly_token_allocation - orgUsage.tenant.token_balance) / orgUsage.tenant.monthly_token_allocation) * 100) : 0;
        return (
          <>
            <h5 style={{ fontWeight: '600', marginBottom: '1.5rem' }}>Token Usage</h5>

            <Row className="mb-4">
              <Col md={3}>
                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#166534' }}>{formatNumber(orgUsage?.tenant?.token_balance)}</div>
                  <div style={{ color: '#166534', fontSize: '0.8rem' }}>Available Balance</div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e40af' }}>{formatNumber(orgUsage?.tenant?.monthly_token_allocation)}</div>
                  <div style={{ color: '#1e40af', fontSize: '0.8rem' }}>Monthly Allocation</div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#92400e' }}>{formatNumber(orgUsage?.thisMonth?.tokensUsed || 0)}</div>
                  <div style={{ color: '#92400e', fontSize: '0.8rem' }}>Used This Month</div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#166534' }}>{orgUsage?.thisMonth?.articleCount || 0}</div>
                  <div style={{ color: '#166534', fontSize: '0.8rem' }}>Articles This Month</div>
                </div>
              </Col>
            </Row>

            <Card className="mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between mb-2">
                  <span>Monthly Usage</span>
                  <span>{formatNumber(orgUsage?.thisMonth?.tokensUsed || 0)} / {formatNumber(orgUsage?.tenant?.monthly_token_allocation)} tokens</span>
                </div>
                <ProgressBar now={usagePercent} variant={usagePercent > 90 ? 'danger' : usagePercent > 70 ? 'warning' : 'success'} />
                <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>{usagePercent}% used</div>
              </Card.Body>
            </Card>

            <Row>
              <Col md={6}>
                <h6 style={{ fontWeight: '600' }}>All-Time Statistics</h6>
                <Table size="sm">
                  <tbody>
                    <tr><td>Total Tokens Used</td><td className="text-end">{formatNumber(orgUsage?.allTime?.tokensUsed || 0)}</td></tr>
                    <tr><td>Total Articles</td><td className="text-end">{formatNumber(orgUsage?.allTime?.articleCount || 0)}</td></tr>
                    <tr><td>Average per Article</td><td className="text-end">{orgUsage?.allTime?.articleCount ? Math.round((orgUsage?.allTime?.tokensUsed || 0) / orgUsage.allTime.articleCount) : '-'}</td></tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={6}>
                <h6 style={{ fontWeight: '600' }}>This Month</h6>
                <Table size="sm">
                  <tbody>
                    <tr><td>Tokens Used</td><td className="text-end">{formatNumber(orgUsage?.thisMonth?.tokensUsed || 0)}</td></tr>
                    <tr><td>Articles Generated</td><td className="text-end">{orgUsage?.thisMonth?.articleCount || 0}</td></tr>
                    <tr><td>Tokens Remaining</td><td className="text-end">{formatNumber(orgUsage?.tenant?.token_balance)}</td></tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </>
        );

      case 'history':
        return (
          <>
            <h5 style={{ fontWeight: '600', marginBottom: '1.5rem' }}>Article History</h5>

            <Table responsive hover>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th>Article</th>
                  <th>Created By</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgHistory.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-4 text-muted">No articles yet</td></tr>
                ) : orgHistory.map((article, idx) => (
                  <tr key={idx}>
                    <td>
                      <div style={{ fontWeight: '500' }}>{article.article_title || 'Untitled'}</div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{article.source_domain}</div>
                    </td>
                    <td>{article.created_by?.display_name || article.created_by?.name || 'Unknown'}</td>
                    <td>
                      <Badge bg={article.status === 'completed' ? 'success' : article.status === 'failed' ? 'danger' : 'warning'}>
                        {article.status}
                      </Badge>
                    </td>
                    <td>{formatDate(article.created_at)}</td>
                    <td>
                      {article.status === 'completed' && article.slug && (
                        <Link to={`/app/article-to-media/${article.slug}`} className="btn btn-outline-primary btn-sm">View</Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        );

      case 'settings':
      default:
        const timezones = [
          'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
          'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
          'Europe/London', 'Europe/Paris', 'Europe/Berlin',
          'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Australia/Sydney', 'UTC'
        ];
        return (
          <>
            <h5 style={{ fontWeight: '600', marginBottom: '1.5rem' }}>Organization Settings</h5>

            {orgSettings && (
              <>
                {/* Organization Info - Read Only */}
                <Card className="mb-4">
                  <Card.Body>
                    <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>Organization Info</h6>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Name</div>
                          <div style={{ fontWeight: '500' }}>{orgSettings.name}</div>
                        </div>
                        <div className="mb-3">
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Slug</div>
                          <div style={{ fontWeight: '500' }}>{orgSettings.slug}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Account Level</div>
                          <div style={{ fontWeight: '500', textTransform: 'capitalize' }}>{orgSettings.account_level}</div>
                        </div>
                        <div className="mb-3">
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Status</div>
                          <Badge bg={orgSettings.is_active ? 'success' : 'secondary'}>
                            {orgSettings.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Plan Details - Read Only */}
                <Card className="mb-4">
                  <Card.Body>
                    <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>Plan Details</h6>
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Seat Count</div>
                          <div style={{ fontWeight: '500' }}>{orgSettings.seat_count}</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Monthly Token Allocation</div>
                          <div style={{ fontWeight: '500' }}>{formatNumber(orgSettings.monthly_token_allocation)}</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Current Balance</div>
                          <div style={{ fontWeight: '500' }}>{formatNumber(orgSettings.token_balance)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* History & Visibility - Editable */}
                <Card className="mb-4">
                  <Card.Body>
                    <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>History & Visibility</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>User History Visibility</Form.Label>
                      <Form.Select
                        value={orgConfig.user_history_visibility}
                        onChange={(e) => setOrgConfig({ ...orgConfig, user_history_visibility: e.target.value })}
                      >
                        <option value="own_only">Own history only - Users can only see their own articles</option>
                        <option value="all_users">All users - Users can see all articles in the organization</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Controls whether regular users can see articles created by other users.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-0">
                      <Form.Check
                        type="switch"
                        id="show-token-costs"
                        label="Show token costs to users"
                        checked={orgConfig.show_token_costs_to_users}
                        onChange={(e) => setOrgConfig({ ...orgConfig, show_token_costs_to_users: e.target.checked })}
                      />
                      <Form.Text className="text-muted">
                        When enabled, users can see the token cost of their articles.
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Invitation Settings - Editable */}
                <Card className="mb-4">
                  <Card.Body>
                    <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>Invitation Settings</h6>
                    <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                      Configure the default expiration time for new invitations.
                    </p>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Days</Form.Label>
                          <Form.Select
                            value={orgConfig.default_invitation_days}
                            onChange={(e) => setOrgConfig({ ...orgConfig, default_invitation_days: parseInt(e.target.value) })}
                          >
                            {[...Array(31)].map((_, i) => (
                              <option key={i} value={i}>{i} day{i !== 1 ? 's' : ''}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Hours</Form.Label>
                          <Form.Select
                            value={orgConfig.default_invitation_hours}
                            onChange={(e) => setOrgConfig({ ...orgConfig, default_invitation_hours: parseInt(e.target.value) })}
                          >
                            {[...Array(24)].map((_, i) => (
                              <option key={i} value={i}>{i} hour{i !== 1 ? 's' : ''}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Timezone - Editable */}
                <Card className="mb-4">
                  <Card.Body>
                    <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>Timezone</h6>
                    <Form.Group className="mb-0">
                      <Form.Label>Organization Timezone</Form.Label>
                      <Form.Select
                        value={orgConfig.timezone}
                        onChange={(e) => setOrgConfig({ ...orgConfig, timezone: e.target.value })}
                      >
                        {timezones.map(tz => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        All dates and times will be displayed in this timezone.
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>

                <div className="d-flex justify-content-end">
                  <Button variant="primary" onClick={handleSaveOrgConfig} disabled={savingOrgConfig}>
                    {savingOrgConfig ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </>
            )}
          </>
        );
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Settings - Spawnry</title>
      </Helmet>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 3rem'
      }}>
        <Container>
          <Row>
            <Col>
              <div className="d-flex align-items-center mb-2">
                <Link to="/dashboard" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', marginRight: '0.5rem' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                </Link>
                <h1 style={{
                  color: 'white',
                  fontWeight: '700',
                  fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                  margin: 0
                }}>
                  Settings
                </h1>
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', margin: 0 }}>
                Manage your profile and preferences
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      <Container style={{ marginTop: '-1.5rem', paddingBottom: '3rem' }}>
        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
            {success}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
            {error}
          </Alert>
        )}

        {/* Tab Navigation */}
        <Card className="mb-4" style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <Card.Body className="p-0">
            <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabSelect} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <Nav.Item>
                <Nav.Link
                  eventKey="profile"
                  style={{
                    color: activeTab === 'profile' ? '#6366f1' : '#64748b',
                    fontWeight: activeTab === 'profile' ? '600' : '400',
                    border: 'none',
                    borderBottom: activeTab === 'profile' ? '2px solid #6366f1' : '2px solid transparent',
                    borderRadius: 0,
                    padding: '1rem 1.5rem',
                    background: 'transparent'
                  }}
                >
                  Profile
                </Nav.Link>
              </Nav.Item>
              {canManageTenant && (
                <Nav.Item>
                  <Nav.Link
                    eventKey="organization"
                    style={{
                      color: activeTab === 'organization' ? '#6366f1' : '#64748b',
                      fontWeight: activeTab === 'organization' ? '600' : '400',
                      border: 'none',
                      borderBottom: activeTab === 'organization' ? '2px solid #6366f1' : '2px solid transparent',
                      borderRadius: 0,
                      padding: '1rem 1.5rem',
                      background: 'transparent'
                    }}
                  >
                    Organization Admin
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </Card.Body>
        </Card>

        {/* Profile Tab Content */}
        {activeTab === 'profile' && (
        <Row>
          <Col lg={8}>
            {/* Profile Section */}
            <Card className="mb-4" style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              <Card.Body className="p-4">
                <h5 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Personal Information</h5>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                  Your personal info is shared across all organizations you belong to.
                </p>

                <Form onSubmit={handleSaveProfile}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={profile.first_name}
                          onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                          placeholder="Enter first name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={profile.last_name}
                          onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                          placeholder="Enter last name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={profile.email}
                      disabled
                    />
                    <Form.Text className="text-muted">
                      Email cannot be changed
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={savingProfile}
                  >
                    {savingProfile ? 'Saving...' : 'Save Profile'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            {/* Display Name Section (for active tenant) */}
            {activeTenant && (
              <Card className="mb-4" style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                <Card.Body className="p-4">
                  <h5 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Display Name</h5>
                  <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                    Your display name in <strong>{activeTenant.name}</strong>. This is how other users in this organization will see you.
                  </p>

                  <Form onSubmit={handleSaveDisplayName}>
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Enter display name"
                      />
                      <Form.Text className="text-muted">
                        Display names must be unique within each organization
                      </Form.Text>
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      disabled={savingDisplayName}
                    >
                      {savingDisplayName ? 'Saving...' : 'Save Display Name'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            )}

            {/* Default Tenant Section */}
            {tenants.length > 1 && (
              <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                <Card.Body className="p-4">
                  <h5 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Default Organization</h5>
                  <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                    Choose which organization to load by default when you log in.
                  </p>

                  <Form onSubmit={handleSavePreferences}>
                    <Form.Group className="mb-3">
                      <Form.Select
                        value={preferences.default_tenant_id}
                        onChange={(e) => setPreferences({ ...preferences, default_tenant_id: e.target.value })}
                      >
                        <option value="">Use most recent</option>
                        {tenants.map((tenant) => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        If set to "Use most recent", the last organization you used will be selected automatically
                      </Form.Text>
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Preference'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Account Info */}
            <Card className="mb-4" style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              <Card.Body className="p-4">
                <h5 style={{ fontWeight: '600', marginBottom: '1rem' }}>Account</h5>

                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Email</div>
                  <div style={{ fontWeight: '500' }}>{profile.email}</div>
                </div>

                {auth?.platform_role && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Platform Role</div>
                    <div style={{ fontWeight: '500', textTransform: 'capitalize' }}>
                      {auth.platform_role.replace('_', ' ')}
                    </div>
                  </div>
                )}

                <div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Organizations</div>
                  <div style={{ fontWeight: '500' }}>{tenants.length}</div>
                </div>
              </Card.Body>
            </Card>

            {/* Organizations List */}
            {tenants.length > 0 && (
              <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                <Card.Body className="p-4">
                  <h5 style={{ fontWeight: '600', marginBottom: '1rem' }}>Your Organizations</h5>

                  {tenants.map((tenant, idx) => (
                    <div
                      key={tenant.id}
                      style={{
                        padding: '0.75rem 0',
                        borderBottom: idx < tenants.length - 1 ? '1px solid #e2e8f0' : 'none'
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div style={{ fontWeight: '500', color: '#1e293b' }}>
                            {tenant.name}
                            {tenant.is_active && (
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#22c55e' }}>
                                (active)
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            {tenant.display_name || 'No display name set'}
                          </div>
                        </div>
                        <span style={{
                          fontSize: '0.75rem',
                          padding: '0.2rem 0.5rem',
                          background: '#f1f5f9',
                          borderRadius: '4px',
                          color: '#64748b',
                          textTransform: 'capitalize'
                        }}>
                          {tenant.role}
                        </span>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
        )}

        {/* Organization Admin Tab Content */}
        {activeTab === 'organization' && canManageTenant && (
          <Row>
            <Col md={3}>
              <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                <Card.Body className="p-3">
                  <Nav className="flex-column">
                    {orgNavItems.map((item) => (
                      <Nav.Link
                        key={item.key}
                        onClick={() => handleOrgSectionChange(item.key)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          color: orgSection === item.key ? 'white' : '#64748b',
                          background: orgSection === item.key ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'transparent',
                          borderRadius: '8px',
                          marginBottom: '0.25rem',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '0.95rem'
                        }}
                      >
                        {item.icon}
                        {item.label}
                      </Nav.Link>
                    ))}
                  </Nav>
                </Card.Body>
              </Card>
            </Col>
            <Col md={9}>
              <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', minHeight: '400px' }}>
                <Card.Body className="p-4">
                  {renderOrgSectionContent()}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      {/* Edit User Modal */}
      <Modal show={showEditUserModal} onHide={() => setShowEditUserModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p className="text-muted mb-3">Editing: {selectedUser.email}</p>
              <Form.Group className="mb-3">
                <Form.Label>Display Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editUserData.display_name}
                  onChange={(e) => setEditUserData({ ...editUserData, display_name: e.target.value })}
                  placeholder="John Smith"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  value={editUserData.role}
                  onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditUserModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleEditUser} disabled={processing}>
            {processing ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Remove User Modal */}
      <Modal show={showRemoveUserModal} onHide={() => setShowRemoveUserModal(false)}>
        <Modal.Header closeButton style={{ background: '#fef2f2' }}>
          <Modal.Title style={{ color: '#dc2626' }}>Remove User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <Alert variant="warning">
                Removing <strong>{selectedUser.display_name || selectedUser.email}</strong> from this organization.
              </Alert>
              <p>How should their name appear on existing articles?</p>
              <Form.Group className="mb-3">
                <Form.Check
                  type="radio"
                  id="attr-keep"
                  label="Keep original name"
                  checked={removeUserData.attribution_mode === 'keep'}
                  onChange={() => setRemoveUserData({ ...removeUserData, attribution_mode: 'keep' })}
                />
                <Form.Check
                  type="radio"
                  id="attr-replace"
                  label="Replace with custom text"
                  checked={removeUserData.attribution_mode === 'replace'}
                  onChange={() => setRemoveUserData({ ...removeUserData, attribution_mode: 'replace' })}
                />
                {removeUserData.attribution_mode === 'replace' && (
                  <Form.Control
                    type="text"
                    className="mt-2 ms-4"
                    value={removeUserData.replacement_text}
                    onChange={(e) => setRemoveUserData({ ...removeUserData, replacement_text: e.target.value })}
                    placeholder="Former Employee"
                  />
                )}
                <Form.Check
                  type="radio"
                  id="attr-hide"
                  label="Hide name entirely"
                  checked={removeUserData.attribution_mode === 'hide'}
                  onChange={() => setRemoveUserData({ ...removeUserData, attribution_mode: 'hide' })}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveUserModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleRemoveUser} disabled={processing}>
            {processing ? 'Removing...' : 'Remove User'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invite User Modal */}
      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Invite User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              value={inviteData.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              placeholder="user@example.com"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={inviteData.role}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
            >
              <option value="user">User</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInviteModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSendInvitation} disabled={processing || !inviteData.email}>
            {processing ? 'Sending...' : 'Send Invitation'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(DashboardSettings);
